import React, { useState } from 'react';
import { Container, Box, Typography, TextField, Button, Paper, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const PageTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '2.5rem',
  color: '#1a1a1a',
  marginBottom: '2rem',
  textAlign: 'center',
});

const FormContainer = styled(Paper)({
  padding: '2rem',
  marginTop: '2rem',
  backgroundColor: '#fafafa',
  border: '1px solid #eee',
});

const StyledButton = styled(Button)({
  backgroundColor: '#1a1a1a',
  color: 'white',
  padding: '1rem 2rem',
  marginTop: '2rem',
  '&:hover': {
    backgroundColor: '#c4a661',
  },
});

const AffiliateRegister = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    phone: '',
    instagram: '',
    tiktok: '',
    youtube: '',
    website: '',
    contentType: '',
    audience: '',
    experience: '',
    bio: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // TODO: Submit affiliate application to backend
      console.log('Form submitted:', formData);
      navigate('/affiliate/pending');
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 8 }}>
        <PageTitle>Affiliate Application</PageTitle>
        
        <FormContainer elevation={0}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={!!user}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Instagram Handle"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  placeholder="@username"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="TikTok Handle"
                  name="tiktok"
                  value={formData.tiktok}
                  onChange={handleChange}
                  placeholder="@username"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="YouTube Channel"
                  name="youtube"
                  value={formData.youtube}
                  onChange={handleChange}
                  placeholder="Channel URL"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Website/Blog"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Primary Content Type</InputLabel>
                  <Select
                    name="contentType"
                    value={formData.contentType}
                    onChange={handleChange}
                    required
                  >
                    <MenuItem value="photos">Photography</MenuItem>
                    <MenuItem value="videos">Videos</MenuItem>
                    <MenuItem value="blog">Blog Posts</MenuItem>
                    <MenuItem value="reviews">Product Reviews</MenuItem>
                    <MenuItem value="mixed">Mixed Content</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Audience Size</InputLabel>
                  <Select
                    name="audience"
                    value={formData.audience}
                    onChange={handleChange}
                    required
                  >
                    <MenuItem value="micro">1K - 10K followers</MenuItem>
                    <MenuItem value="small">10K - 50K followers</MenuItem>
                    <MenuItem value="medium">50K - 100K followers</MenuItem>
                    <MenuItem value="large">100K - 500K followers</MenuItem>
                    <MenuItem value="xlarge">500K+ followers</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Content Creation Experience</InputLabel>
                  <Select
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    required
                  >
                    <MenuItem value="beginner">Less than 1 year</MenuItem>
                    <MenuItem value="intermediate">1-3 years</MenuItem>
                    <MenuItem value="experienced">3-5 years</MenuItem>
                    <MenuItem value="expert">5+ years</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  required
                  placeholder="Tell us about yourself and why you'd be a great affiliate partner..."
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <StyledButton
                type="submit"
                variant="contained"
                size="large"
              >
                Submit Application
              </StyledButton>
            </Box>
          </form>
        </FormContainer>
      </Box>
    </Container>
  );
};

export default AffiliateRegister;
